import React, { FC, useEffect, useMemo } from 'react';

import { Box, Grid } from '@mui/material';
import { useFormik } from 'formik';

import {
  adminActions,
  adminContractDetails,
  adminRejectContract,
  adminRequestContractDetails,
  adminSelfRejectContract,
  successOrSelector,
  WorkshiftStatus,
} from '@pharmaplan/common';

import CancelContractTimes from '../../../SelfService/ServiceWorkshiftDetails/CancelContractRequestModal/CancelContractTimes';
import useViewEditContract from '../../../../hooks/workshift/useViewEditContract';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { resetDialog } from '../../../../reducers/dialogReducer';
import LongContractCard from '../../../common/LongContractCard';
import useSelfService from '../../../../hooks/useSelfService';
import genericClasses from '../../../../theme/GenericClasses';
import { Constants } from '../../../../helpers/Constants';
import CustomButton from '../../../common/CustomButton';
import strings from '../../../../localization';

import styles from './styles';

interface IAdminIgnoreContractModal {
  longTermContractId?: string;
  successAction: string;
  professionalId: string;
}

const AdminIgnoreContractModal: FC<IAdminIgnoreContractModal> = ({
  longTermContractId,
  professionalId,
  successAction,
}) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();
  const { intervalsInitial } = useViewEditContract();

  const params = {
    longTermContractId: longTermContractId ?? '',
    professionalId,
  };

  const success = useAppSelector((state) =>
    successOrSelector([adminActions.adminRejectContract, adminActions.adminSelfRejectContract], state));

  const events = useAppSelector(adminContractDetails);
  const { requests, longTermShiftContract, counterShiftContract, firstName, lastName, postalCode, profilePicture } = events ?? {};

  const {
    allowanceNotes,
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
  } = longTermShiftContract ?? {};

  const {
    weekDayPaidHourlyRate: counterWeekDayPaidHourlyRate,
    weekendPaidHourlyRate: counterWeekendPaidHourlyRate,
    allowanceNotes: counterAllowanceNotes,
  } = counterShiftContract ?? {};

  const requestedData = useMemo(
    () =>
      events?.requests.map((item) =>
        ({
          ...item,
          status: WorkshiftStatus.Requested,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requests],
  );

  const userData = {
    name: `${firstName} ${lastName}`,
    postalCode,
    profilePicture,
    longTermShiftContract,
  };

  const handleClose = () => {
    dispatch(resetDialog());
  };

  const handleSubmit = () => {
    if (isSelfService) {
      dispatch(adminSelfRejectContract(params));
    } else {
      dispatch(adminRejectContract(params));
    }
  };

  const formik = useFormik({
    initialValues: {
      allowanceNotes,
      proposedAllowance: counterAllowanceNotes,
      workshiftIds: [],
      weekDayPaidHourlyRate:
        counterWeekDayPaidHourlyRate ?? weekDayPaidHourlyRate,
      weekendPaidHourlyRate:
        counterWeekendPaidHourlyRate ?? weekendPaidHourlyRate,
    },
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    dispatch(
      adminRequestContractDetails(params),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (requestedData) {
      formik.setValues({
        ...formik.values,
        ...intervalsInitial(requestedData),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedData]);

  useEffect(() => {
    if (success) {
      dispatch(resetDialog());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.subContainer}>
        <Grid columnSpacing="15px" container>
          <Grid md={4} xs={12} item>
            <LongContractCard
              formik={formik}
              event={userData}
              successAction={successAction}
            />
          </Grid>
          <Grid md={8} xs={12} item>
            <CancelContractTimes
              shifts={requestedData ?? []}
              formik={formik}
              counterShifts={[]}
            />
          </Grid>
        </Grid>
      </Box>
      <Box sx={[styles.buttonContainer, genericClasses.flex]}>
        <CustomButton
          label={strings.cancel}
          onClick={handleClose}
          variant={Constants.variant.outlined}
        />
        <CustomButton
          customClass={styles.saveButton}
          onClick={formik.handleSubmit}
          label={strings.ignore}
        />
      </Box>
    </Box>
  );
};

export default AdminIgnoreContractModal;
